<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Text, Container, Button, Stack } from '@Shelly/shelly-design-system';
	import ActiveTimePreviewWithControls from './components/ActiveTimePreviewWithControls.svelte';
	import AddEditInterval from '../Process/ActiveTime/AddEditInterval.svelte';
	import type { ISceneObj } from '../../../../types/SceneType';
	import { edit_scene_active_time, delete_active_time_by_ind } from '../manage_scene_elements';

	import {
		extractEmptyInterval,
		fillIntervalMatrix,
		findEmptyIntervals,
	} from '../../helpers/intervals_helpers';
	import { calculateWhenEnd } from '../../../../helpers/interface_helpers';
	import { real_duration_from_negative, _calculate_duration } from '../../../../helpers/utilities';

	export let scene_object: ISceneObj;
	export let hideInterval = false;
	export let showEditActiveTime = false;
	export let editedInterval = null;
	export let stopConfirmationModal = false;
	export let backdropCloseModal = false;

	const dispatch = createEventDispatcher();

	const extract_intervals = (scene_object) => {
		let intervals = [];
		if (scene_object?.if?.and) {
			//1. if find element with property indailyint it is one interval only (single object)
			//2. else we have two ors in the and and we need to find which one has elements with property indailyint (it is an array)
			let find_indailyint = scene_object?.if?.and?.find((el) => el && el.indailyint);
			if (find_indailyint) {
				intervals = [find_indailyint];
			} else {
				intervals = scene_object?.if?.and?.find((el) => el?.or?.every((l) => l.indailyint))
					?.or || [
					{
						indailyint: {
							dows: '1 2 3 4 5 6 7',
							startat: '00:00',
							dur: 1439, // because 1440 calculates end 00:00 -> we want to display 00:00 - 23:59 defaul value
						},
					},
				];
				//find or with indailyints and map it to needed array format
			}
		} else {
			/**the main interval -> whole weeks -> scenes' default if user never changed it**/
			intervals = [
				{
					indailyint: {
						dows: '1 2 3 4 5 6 7',
						startat: '00:00',
						dur: 1439, // because 1440 calculates end 00:00 -> we want to display 00:00 - 23:59 defaul value
					},
				},
			];
		}
		return intervals;
	};

	const first_empty_interval = () => {
		if (intervals?.length > 0) {
			intervalMatrix = fillIntervalMatrix(intervals, 'scene_active_time');

			emptyHoursForWeek = findEmptyIntervals(intervalMatrix);

			disabled = !Object.values(Object.values(emptyHoursForWeek)).some((el: any) => el.length > 0);
			if (!disabled) {
				first_empty = extractEmptyInterval(emptyHoursForWeek);
				console.log(':i:, ', first_empty);
			}
		} else {
			//		console.log('reset first empTy');
		}
	};

	let intervals = extract_intervals(scene_object);

	console.log('INTERVALS ', intervals);
	let intervalMatrix = {},
		emptyHoursForWeek = {},
		first_empty = { isin: [], time: [] };

	let interval = {
		type: 'add',
		index: 0,
		isin: intervals[0]?.indailyint?.dows?.split(' ')?.map((e) => Number(e) - 1),
		time: [
			intervals[0]?.indailyint?.startat,
			calculateWhenEnd(intervals[0]?.indailyint?.startat, intervals[0]?.indailyint?.dur),
		],
	};

	first_empty_interval();

	if (first_empty?.isin?.length > 0 && first_empty?.time?.length === 2) {
		interval.isin = first_empty?.isin;
		interval.time = first_empty?.time;
		interval.index = intervals.length;
	}

	const handle_cancel = () => {
		showEditActiveTime = false;
	};

	const handle_save = (e) => {
		const { interval } = e.detail;
		let interval_duration = _calculate_duration(interval.time);
		let new_active_time = {
			indailyint: {
				dows: interval.isin.map((el) => Number(el) + 1).join(' '),
				startat: interval.time[0],
				dur:
					interval_duration < 0
						? real_duration_from_negative(interval_duration)
						: interval_duration,
			},
		};

		scene_object = edit_scene_active_time(
			scene_object,
			new_active_time,
			interval.type,
			interval.index,
		);

		showEditActiveTime = false;
		intervals = extract_intervals(scene_object);
		first_empty_interval();
		dispatch('save');
	};

	const handle_add = () => {
		showEditActiveTime = true;
		interval = {
			type: 'add',
			index: intervals.length,
			isin: first_empty?.isin,
			time: first_empty?.time,
		};
	};

	const handle_edit = (active_time, ind) => {
		showEditActiveTime = true;

		interval = {
			type: 'edit',
			index: ind,
			isin: active_time?.indailyint?.dows?.split(' ').map((e) => Number(e) - 1),
			time: [
				active_time?.indailyint?.startat,
				calculateWhenEnd(active_time?.indailyint?.startat, active_time?.indailyint?.dur),
			],
		};
	};

	const handle_delete = (ind) => {
		scene_object = delete_active_time_by_ind(scene_object, ind);
		intervals = extract_intervals(scene_object);
		first_empty_interval();
		console.log('INTERVALS ', intervals);
	};

	$: disabled = first_empty?.isin?.length === 0 || first_empty?.time?.length === 0;

	$: if (editedInterval) {
		handle_edit(editedInterval.active_time.interval, editedInterval.ind);
	}
</script>

{#if !hideInterval}
	<Container padding="scrollPaddings">
		<Text styleType="h2" class="mb-s">
			{$format('scene_preview_active_time')}
		</Text>
		<Stack gap="s">
			{#each intervals as active_time, ind (active_time)}
				<ActiveTimePreviewWithControls
					{active_time}
					disableDelete={intervals.length === 1}
					on:edit={() => handle_edit(active_time, ind)}
					on:delete={() => handle_delete(ind)}
				/>
			{/each}
		</Stack>
		<div class="mt-m text-center">
			<Button variant="secondary" size="m" {disabled} on:click={(e) => handle_add()}>
				{$format('button_add_interval', {
					default: 'Add interval',
				})}
			</Button>
		</div>
	</Container>
{/if}

{#if showEditActiveTime}
	<AddEditInterval
		backdropClose={backdropCloseModal}
		{stopConfirmationModal}
		{intervals}
		{interval}
		on:cancel={() => handle_cancel()}
		on:save={(e) => handle_save(e)}
	/>
{/if}
